import { useState, useEffect, ReactNode } from 'react';
import { useSlideOver } from 'xstate/hooks/useSlideOver';
import { closeSlideOver } from 'xstate/actors/SlideOverActor';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslations } from 'next-intl';

const SlideOver = () => {
  const t = useTranslations();
  const { isOpen } = useSlideOver();
  // Starts hidden, like the panel itself. Starting shown made the empty
  // fixed panel sit in the page for a moment on every load, then drop out
  // of it, which counted as a layout shift.
  const [showContainer, setShowContainer] = useState(isOpen);

  const containerStyle = {
    visible: 'fixed right-0 max-w-full flex inset-y-0 pt-14',
    hidden: 'hidden',
  };

  const drawerStyle = {
    visible:
      'w-screen max-w-md transition ease-in-out duration-500 sm:duration-300 translate-x-0',
    hidden:
      'w-screen max-w-md transition ease-in-out duration-500 sm:duration-300 translate-x-full',
  };

  const backgroundOverlayStyle = {
    visible: 'fixed inset-0 bg-black/30 transition-opacity',
    hidden: '',
  };

  const handleCloseBtnClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.preventDefault();
    if (isOpen) closeSlideOver();
  };

  const handleClickAway = () => {
    if (isOpen) closeSlideOver();
  };

  // Generic slide-over shell — no feature currently opens it (boxes and
  // collections moved to the Modal). Switch on `view` here to render a title /
  // body when a future feature needs a slide-over.
  const renderTitle = (): ReactNode => null;
  const renderSlideOver = (): ReactNode => null;

  /**
   * The container would normally stay fixed if we didn't remove which would prevent click
   * on DOM elements behind it. To prevent this and conserve the animation, we hide the
   * container after a timeout.
   */
  useEffect(() => {
    if (isOpen) {
      setShowContainer(true);
      return;
    }

    const timer = setTimeout(() => {
      setShowContainer(false);
    }, 350);

    return () => clearTimeout(timer);
  }, [isOpen]);

  return (
    <div
      className={isOpen ? 'fixed overflow-hidden inset-0 z-10' : ''}
      aria-labelledby="slide-over-title"
      role="dialog"
      aria-modal="true"
    >
      <div className={isOpen ? 'absolute overflow-hidden inset-0' : ''}>
        <div
          className={
            isOpen
              ? backgroundOverlayStyle.visible
              : backgroundOverlayStyle.hidden
          }
          aria-hidden="true"
          onClick={handleClickAway}
        ></div>
        <div
          className={
            showContainer ? containerStyle.visible : containerStyle.hidden
          }
        >
          <div className={isOpen ? drawerStyle.visible : drawerStyle.hidden}>
            <div className="h-full flex-col bg-white shadow-xl overflow-y-scroll">
              <div className="p-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                  {/* Center title container */}
                  <div className="flex-1 flex justify-center">
                    <h2
                      className="text font-medium text-gray-900 w-full text-center"
                      id="slide-over-title"
                    >
                      {renderTitle()}
                    </h2>
                  </div>

                  {/* Close button (right) */}
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500 focus:outline-slate-900"
                    onClick={handleCloseBtnClick}
                  >
                    <span className="sr-only">{t('common.close')}</span>
                    <FontAwesomeIcon
                      icon={faXmark}
                      className={styles.iconColor}
                      size="lg"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </div>

              {renderSlideOver()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  iconColor: 'text-[#6C727E]',
};

export default SlideOver;
